import moment from 'moment';
import React, { useState } from 'react';
import { Avatar } from '../../../../../components';
import { AvatarVariant } from '../../../../../components/Avatar/Avatar';
import {
  EmailBranch,
  ParticipantGeneralInfo,
} from '../../../../../store/deals/deals.types';
import { convertValueIntoTime } from '../../utils/utils';
import { ThreadDataSkeleton } from '../skeletons/ThreadDataSkeleton';
import { infoIcon, lifeCycleIcon } from './assets';

type Props = {
  selectedBranch: EmailBranch | null;
  isLoading: boolean;
};

export const ThreadData: React.FC<Props> = ({
  selectedBranch,
  isLoading,
}): JSX.Element => {
  const [userTooltipOpen, setUserTooltipOpen] = useState(false);
  const [otherTooltipOpen, setOtherTooltipOpen] = useState(false);

  const toggleUserTooltip = () => {
    if (selectedBranch?.participantsBySide?.userSide?.length) {
      setUserTooltipOpen(!userTooltipOpen);
    }
  };

  const toggleOtherTooltip = () => {
    if (selectedBranch?.participantsBySide?.otherSide?.length) {
      setOtherTooltipOpen(!otherTooltipOpen);
    }
  };

  const wordsUser = selectedBranch?.avgWordCountUser || 0;
  const wordsOther = selectedBranch?.avgWordCountOtherSide || 0;
  const timeUser = selectedBranch?.avgUserReplyTimeHours || 0;
  const timeOther = selectedBranch?.avgOtherSideReplyTimeHours || 0;

  // const wordsUser = 4344;
  // const wordsOther = 11184.58823529411765;
  // const timeUser = 65.23;
  // const timeOther = 34.355;

  // const wordsUser = 0;
  // const wordsOther = 0;
  // const timeUser = 0;
  // const timeOther = 0;

  const defineBarWidth = (
    you: number | null,
    other: number | null,
    isWords?: boolean
  ) => {
    const baseValue = isWords ? 50 : 8;
    let barYouWidth = 0;
    let barOtherWidth = 0;
    if ((you || you === 0) && (other || other === 0)) {
      if (you >= baseValue && other < baseValue) {
        barYouWidth = you * 100;
        barOtherWidth = (other / you) * 100;
      } else if (you < baseValue && other >= baseValue) {
        barYouWidth = (you / other) * 100;
        barOtherWidth = other * 100;
      } else if (you < baseValue && other < baseValue) {
        barYouWidth = (you / baseValue) * 100;
        barOtherWidth = (other / baseValue) * 100;
      } else if (you > baseValue && other > baseValue) {
        if (you > other) {
          barYouWidth = you * 100;
          barOtherWidth = (other / you) * 100;
        } else {
          barYouWidth = (you / other) * 100;
          barOtherWidth = other * 100;
        }
      }
    }

    if (barYouWidth > 100) {
      barYouWidth = 100;
    }

    if (barOtherWidth > 100) {
      barOtherWidth = 100;
    }

    return { barYouWidth, barOtherWidth };
  };

  const {
    barYouWidth: timeBarYou,
    barOtherWidth: timeBarOther,
  } = defineBarWidth(timeUser, timeOther);

  const {
    barYouWidth: wordBarYou,
    barOtherWidth: wordBarOther,
  } = defineBarWidth(wordsUser, wordsOther, true);

  const attrs = {
    wrapper: { className: 'thread-data' },
    header: {
      wrapper: { className: 'thread-data__header' },
      title: { className: 'thread-data__header-title' },
      duration: {
        wrapper: { className: 'thread-data__header__duration' },

        row: {
          wrapper: {
            className: 'thread-data__header__duration__row',
          },

          label: {
            wrapper: {
              className: 'thread-data__header__duration__row__label',
            },

            icon: {
              src: infoIcon,
              className: 'thread-data__header__duration__row__label-icon',
            },

            tooltip: {
              className: 'thread-data__header__duration__row__label-tooltip',
            },
          },

          value: { className: 'thread-data__header__duration__row-value' },
        },
      },
    },
    body: {
      wrapper: { className: 'thread-data__body' },
      leftSection: {
        wrapper: { className: 'thread-data__body__left-section' },
        user: {
          wrapper: { className: 'thread-data__body__left-section__user' },
          title: {
            className: 'thread-data__body__left-section__user-title',
          },
          avatars: {
            wrapper: {
              className: 'thread-data__body__left-section__user__avatars',
              onMouseEnter: toggleUserTooltip,
              onMouseLeave: toggleUserTooltip,
            },
            avatarOne: {
              className: 'thread-data__body__left-section__user__avatars-one',
              isTwentyPx: true,
            },
            avatarTwo: {
              className: 'thread-data__body__left-section__user__avatars-two',
              isTwentyPx: true,
            },
            plusIcon: {
              className: 'thread-data__body__left-section__user__avatars-plus',
            },
            tooltip: {
              wrapper: {
                className: userTooltipOpen
                  ? 'left-open thread-data__tooltip'
                  : 'thread-data__tooltip',
              },
              participant: {
                className: 'thread-data__tooltip__participant',
              },
              avatar: {
                className: 'thread-data__tooltip__participant-avatar',
                isTwentyPx: true,
              },
              name: {
                className: 'thread-data__tooltip__participant-name',
              },
            },
          },
        },
        replyTime: {
          wrapper: { className: 'thread-data__body__left-section__time' },
          label: {
            className: 'thread-data__body__left-section__time-label',
          },
          body: {
            wrapper: {
              className: 'thread-data__body__left-section__time__body',
            },
            bar: {
              className: 'thread-data__body__left-section__time__body-line',
              style: { width: `${timeBarYou * 0.7 + 3}%` },
            },
            value: {
              className: 'thread-data__body__left-section__time__body-value',
            },
            boldValue: {
              className:
                'thread-data__body__left-section__time__body-value-bold',
            },
            notAvailable: {
              className: 'thread-data__body__left-section__time__body-n-a',
            },
          },
        },

        wordCount: {
          wrapper: { className: 'thread-data__body__left-section__words' },
          label: {
            className: 'thread-data__body__left-section__words-label',
          },
          body: {
            wrapper: {
              className: 'thread-data__body__left-section__words__body',
            },
            bar: {
              className: 'thread-data__body__left-section__words__body-line',
              style: { width: `${wordBarYou * 0.7 + 3}%` },
            },
            value: {
              className: 'thread-data__body__left-section__words__body-value',
            },
            boldValue: {
              className:
                'thread-data__body__left-section__words__body-value-bold',
            },

            notAvailable: {
              className: 'thread-data__body__left-section__words__body-n-a',
            },
          },
        },
      },

      divider: { className: 'thread-data__body__divider' },

      rightSection: {
        wrapper: { className: 'thread-data__body__right-section' },

        user: {
          wrapper: { className: 'thread-data__body__right-section__user' },
          title: {
            className: 'thread-data__body__right-section__user-title',
          },
          avatars: {
            wrapper: {
              className: 'thread-data__body__right-section__user__avatars',
              onMouseEnter: toggleOtherTooltip,
              onMouseLeave: toggleOtherTooltip,
            },
            avatarOne: {
              className: 'thread-data__body__right-section__user__avatars-one',
              isTwentyPx: true,
            },
            avatarTwo: {
              className: 'thread-data__body__right-section__user__avatars-two',
              isTwentyPx: true,
            },
            plusIcon: {
              className: 'thread-data__body__right-section__user__avatars-plus',
            },
            tooltip: {
              wrapper: {
                className: otherTooltipOpen
                  ? 'right-open thread-data__tooltip'
                  : 'thread-data__tooltip',
              },
              participant: {
                className: 'thread-data__tooltip__participant',
              },
              avatar: {
                className: 'thread-data__tooltip__participant-avatar',
                isTwentyPx: true,
              },
              name: {
                className: 'thread-data__tooltip__participant-name',
              },
            },
          },
        },
        replyTime: {
          wrapper: { className: 'thread-data__body__right-section__time' },
          label: {
            className: 'thread-data__body__right-section__time-label',
          },
          body: {
            wrapper: {
              className: 'thread-data__body__right-section__time__body',
            },
            bar: {
              className: 'thread-data__body__right-section__time__body-line',
              style: { width: `${timeBarOther * 0.7 + 3}%` },
            },
            value: {
              className: 'thread-data__body__right-section__time__body-value',
            },
            boldValue: {
              className:
                'thread-data__body__right-section__time__body-value-bold',
            },
            notAvailable: {
              className: 'thread-data__body__right-section__time__body-n-a',
            },
          },
        },

        wordCount: {
          wrapper: { className: 'thread-data__body__right-section__words' },
          label: {
            className: 'thread-data__body__right-section__words-label',
          },
          body: {
            wrapper: {
              className: 'thread-data__body__right-section__words__body',
            },
            bar: {
              className: 'thread-data__body__right-section__words__body-line',
              style: { width: `${wordBarOther * 0.7 + 3}%` },
            },
            value: {
              className: 'thread-data__body__right-section__words__body-value',
            },
            boldValue: {
              className:
                'thread-data__body__right-section__words__body-value-bold',
            },

            notAvailable: {
              className: 'thread-data__body__right-section__words__body-n-a',
            },
          },
        },
      },
    },
  };

  const createDurationRow = (label: string, value: string) => {
    const defineTimeRange = () => {
      let from = '';
      let to = '';

      if (selectedBranch?.emails?.length && selectedBranch.emails.length > 1) {
        from = moment(selectedBranch.emails[0].sentAt).format('MMM D, YYYY');

        to = moment(
          selectedBranch.emails[selectedBranch.emails.length - 1]?.sentAt
        ).format('MMM D, YYYY');
      }

      return { from, to };
    };

    const { from, to } = defineTimeRange();

    const row = (
      <div {...attrs.header.duration.row.wrapper}>
        <div {...attrs.header.duration.row.label.wrapper}>
          {label}{' '}
          {label === 'Perceived Authority' ? (
            <>
              <img {...attrs.header.duration.row.label.icon} alt="" />
              <div {...attrs.header.duration.row.label.tooltip}>
                Your side’s average Perceived Authority in the thread is based
                on the participants’ Upper-Hand results throughout the thread.
              </div>
            </>
          ) : null}
          {label === 'Total Duration' ? (
            <div
              {...attrs.header.duration.row.label.tooltip}
              style={{ maxWidth: '250px' }}
            >
              {value === 'N/A' || !from?.length || !to?.length ? (
                <>This thread contains only 1 message</>
              ) : (
                <>
                  <img src={lifeCycleIcon} alt="" /> {from} - {to}
                </>
              )}
            </div>
          ) : null}
        </div>
        <div {...attrs.header.duration.row.value}>{value}</div>
      </div>
    );

    return row;
  };

  const defineDuration = () => {
    const duration = selectedBranch?.branchDurationWeeks;

    const durationLessThenWeekCheck =
      typeof selectedBranch?.branchDurationWeeks === 'number' &&
      selectedBranch.branchDurationWeeks < 1;

    const competence = selectedBranch?.userPerceivedCompetence;
    const result = (
      <>
        {createDurationRow(
          'Total Duration',
          `${
            typeof duration === 'number'
              ? `${
                  durationLessThenWeekCheck
                    ? '< 1 week'
                    : `${duration.toFixed(0)} ${
                        duration === 1 ? 'week' : 'weeks'
                      }`
                }`
              : 'N/A'
          }`
        )}

        {createDurationRow(
          'Perceived Authority',
          `${
            typeof competence === 'number'
              ? `${(competence * 100).toFixed(0)} %`
              : 'N/A'
          } `
        )}
      </>
    );
    return result;
  };

  const createAvatarSection = (
    array: ParticipantGeneralInfo[] | null | undefined,
    isUserSide: boolean
  ) => {
    let result = null;

    const createTooltip = () => {
      let userTooltip: JSX.Element | null = null;
      let otherTooltip: JSX.Element | null = null;

      if (array?.length) {
        if (isUserSide) {
          const tooltipData = (
            <div {...attrs.body.leftSection.user.avatars.tooltip.wrapper}>
              {array.map((el, i) => (
                <div
                  {...attrs.body.leftSection.user.avatars.tooltip.participant}
                  key={`${el.metadata.name}${i}`}
                >
                  <Avatar
                    image={el?.metadata?.avatarSrc || ''}
                    userName={el?.metadata?.name}
                    {...attrs.body.leftSection.user.avatars.tooltip.avatar}
                    variant={i % 2 === 0 ? 'primaryL' : 'primaryD'}
                  />
                  <span {...attrs.body.leftSection.user.avatars.tooltip.name}>
                    {el?.metadata?.name}
                  </span>
                </div>
              ))}
            </div>
          );

          userTooltip = tooltipData;
        } else {
          const tooltipData = (
            <div {...attrs.body.rightSection.user.avatars.tooltip.wrapper}>
              {array.map((el, i) => (
                <div
                  {...attrs.body.rightSection.user.avatars.tooltip.participant}
                  key={`${el.metadata.name}${i}`}
                >
                  <Avatar
                    image={el?.metadata?.avatarSrc || ''}
                    userName={el?.metadata?.name}
                    variant={i % 2 === 0 ? 'secondaryL' : 'secondaryD'}
                    {...attrs.body.rightSection.user.avatars.tooltip.avatar}
                  />
                  <span {...attrs.body.rightSection.user.avatars.tooltip.name}>
                    {el?.metadata?.name}
                  </span>
                </div>
              ))}
            </div>
          );

          otherTooltip = tooltipData;
        }
      }
      return { userTooltip, otherTooltip };
    };

    const { userTooltip, otherTooltip } = createTooltip();

    const addVariantType = (index: number, userSide?: boolean) => {
      let variantType: AvatarVariant = '';

      if (userSide) {
        if (index === 0) {
          variantType = 'primaryL';
        } else if (index === 1) {
          variantType = 'primaryD';
        }
      } else if (!userSide) {
        if (index === 0) {
          variantType = 'secondaryL';
        } else if (index === 1) {
          variantType = 'secondaryD';
        }
      }

      return variantType;
    };

    if (array?.length) {
      if (array.length === 1) {
        const singleAvatar = array.map((el) => (
          <Avatar
            key={el.email}
            image={el.metadata.avatarSrc || ''}
            userName={el.metadata.name || ''}
            className="thread-data__body__left-section__user__avatars-two"
            isTwentyPx
          />
        ));

        result = isUserSide ? (
          <div {...attrs.body.leftSection.user.avatars.wrapper}>
            {singleAvatar}
            {userTooltip}
          </div>
        ) : (
          <div {...attrs.body.rightSection.user.avatars.wrapper}>
            {singleAvatar}
            {otherTooltip}
          </div>
        );
      } else if (array.length === 2) {
        const avatars = array.map((el, i) => (
          <Avatar
            key={el.email}
            image={el.metadata.avatarSrc || ''}
            userName={el.metadata.name || ''}
            className={
              i === 0
                ? 'thread-data__body__left-section__user__avatars-one'
                : 'thread-data__body__left-section__user__avatars-two'
            }
            variant={el.metadata.avatarSrc ? '' : addVariantType(i, isUserSide)}
            isTwentyPx
          />
        ));

        result = isUserSide ? (
          <div {...attrs.body.leftSection.user.avatars.wrapper}>
            {avatars}
            {userTooltip}
          </div>
        ) : (
          <div {...attrs.body.rightSection.user.avatars.wrapper}>
            {avatars}
            {otherTooltip}
          </div>
        );
      } else if (array.length > 2) {
        const slicedAvatarsArray = array
          .slice(0, 2)
          .map((el, i) => (
            <Avatar
              key={el.email}
              image={el.metadata.avatarSrc || ''}
              userName={el.metadata.name || ''}
              className={
                i === 0
                  ? 'thread-data__body__left-section__user__avatars-one'
                  : 'thread-data__body__left-section__user__avatars-two'
              }
              variant={
                el.metadata.avatarSrc ? '' : addVariantType(i, isUserSide)
              }
              isTwentyPx
            />
          ));

        result = isUserSide ? (
          <div {...attrs.body.leftSection.user.avatars.wrapper}>
            {slicedAvatarsArray}{' '}
            <span {...attrs.body.leftSection.user.avatars.plusIcon}>
              +{array.length - 2}
            </span>
            {userTooltip}
          </div>
        ) : (
          <div {...attrs.body.rightSection.user.avatars.wrapper}>
            {slicedAvatarsArray}{' '}
            <span {...attrs.body.rightSection.user.avatars.plusIcon}>
              +{array.length - 2}
            </span>
            {otherTooltip}
          </div>
        );
      }
    }

    return result;
  };

  const convertWordsValue = (value: number | null) => {
    let result: number | string | null = 0;
    if (value || value === 0) {
      if (value.toString().includes('.')) {
        if (+value.toString().split('.')[1].slice(0, 1) === 0) {
          result = +value.toFixed(0);
        } else if (
          +value.toString().split('.')[0].slice(0, 1) >= 1 &&
          +value.toString().split('.')[1].slice(0, 1) !== 0
        ) {
          result = +value.toFixed(1);
        }
      } else {
        result = value >= 1000 ? value.toLocaleString('en-US') : value;
      }
    }

    return result;
  };

  const duration = defineDuration();

  const yourSideAvatars = createAvatarSection(
    selectedBranch?.participantsBySide?.userSide,
    true
  );

  const otherSideAvatars = createAvatarSection(
    selectedBranch?.participantsBySide?.otherSide,
    false
  );

  const timeValueYou =
    selectedBranch?.avgUserReplyTimeHours ||
    (typeof selectedBranch?.avgUserReplyTimeHours === 'number' &&
      selectedBranch?.avgUserReplyTimeHours > 0)
      ? convertValueIntoTime(timeUser)
      : 'N/A';

  const timeValueOther =
    selectedBranch?.avgOtherSideReplyTimeHours ||
    (typeof selectedBranch?.avgOtherSideReplyTimeHours === 'number' &&
      selectedBranch?.avgOtherSideReplyTimeHours > 0)
      ? convertValueIntoTime(timeOther)
      : 'N/A';

  const timeUserSideToShow =
    timeValueYou && timeValueYou !== 'N/A' ? (
      <div {...attrs.body.leftSection.replyTime.body.value}>
        <span {...attrs.body.leftSection.replyTime.body.boldValue}>
          {timeValueYou === '< 1 min' ? '< 1' : timeValueYou.split(' ')[0]}
        </span>{' '}
        {timeValueYou === '< 1 min' ? 'min' : timeValueYou.split(' ')[1]}
      </div>
    ) : (
      <div {...attrs.body.leftSection.replyTime.body.notAvailable}>N/A</div>
    );

  const timeOtherSideToShow =
    timeValueOther && timeValueOther !== 'N/A' ? (
      <div {...attrs.body.rightSection.replyTime.body.value}>
        <span {...attrs.body.rightSection.replyTime.body.boldValue}>
          {timeValueOther === '< 1 min' ? '< 1' : timeValueOther.split(' ')[0]}
        </span>{' '}
        {timeValueOther === '< 1 min' ? 'min' : timeValueOther.split(' ')[1]}
      </div>
    ) : (
      <div {...attrs.body.rightSection.replyTime.body.notAvailable}>N/A</div>
    );

  const wordsUserSide = selectedBranch?.avgWordCountUser ? (
    <div {...attrs.body.leftSection.wordCount.body.value}>
      <span {...attrs.body.leftSection.wordCount.body.boldValue}>
        {convertWordsValue(wordsUser)}
      </span>{' '}
      {convertWordsValue(wordsUser) === 1 ? 'word' : 'words'}
    </div>
  ) : (
    <div {...attrs.body.leftSection.wordCount.body.notAvailable}>N/A</div>
  );

  const wordsOtherSide = selectedBranch?.avgWordCountOtherSide ? (
    <div {...attrs.body.rightSection.wordCount.body.value}>
      <span {...attrs.body.rightSection.wordCount.body.boldValue}>
        {convertWordsValue(wordsOther)}
      </span>{' '}
      {convertWordsValue(wordsOther) === 1 ? 'word' : 'words'}
    </div>
  ) : (
    <div {...attrs.body.rightSection.wordCount.body.notAvailable}>N/A</div>
  );

  const leftSection = (
    <div {...attrs.body.leftSection.wrapper}>
      <div {...attrs.body.leftSection.user.wrapper}>
        <span {...attrs.body.leftSection.user.title}>My side</span>
        {yourSideAvatars}
      </div>
      <div {...attrs.body.leftSection.replyTime.wrapper}>
        <div {...attrs.body.leftSection.replyTime.label}>Avg. Reply Time </div>
        <div {...attrs.body.leftSection.replyTime.body.wrapper}>
          <div {...attrs.body.leftSection.replyTime.body.bar} />
          {timeUserSideToShow}
        </div>
      </div>
      <div {...attrs.body.leftSection.wordCount.wrapper}>
        <div {...attrs.body.leftSection.wordCount.label}>Avg. Word Count </div>
        <div {...attrs.body.leftSection.wordCount.body.wrapper}>
          <div {...attrs.body.leftSection.wordCount.body.bar} />
          {wordsUserSide}
        </div>
      </div>
    </div>
  );

  const rightSection = (
    <div {...attrs.body.rightSection.wrapper}>
      <div {...attrs.body.rightSection.user.wrapper}>
        <span {...attrs.body.rightSection.user.title}>Other side</span>
        <span {...attrs.body.rightSection.user.avatars.wrapper}>
          {otherSideAvatars}
        </span>
      </div>
      <div {...attrs.body.rightSection.replyTime.wrapper}>
        <div {...attrs.body.rightSection.replyTime.label}>Avg. Reply Time </div>
        <div {...attrs.body.rightSection.replyTime.body.wrapper}>
          <div {...attrs.body.rightSection.replyTime.body.bar} />
          {timeOtherSideToShow}
        </div>
      </div>
      <div {...attrs.body.rightSection.wordCount.wrapper}>
        <div {...attrs.body.rightSection.wordCount.label}>Avg. Word Count </div>
        <div {...attrs.body.rightSection.wordCount.body.wrapper}>
          <div {...attrs.body.rightSection.wordCount.body.bar} />
          {wordsOtherSide}
        </div>
      </div>
    </div>
  );

  return (
    <ThreadDataSkeleton isLoading={isLoading}>
      <div {...attrs.wrapper}>
        <div {...attrs.header.wrapper}>
          <div {...attrs.header.title}>Thread Data</div>
          <div {...attrs.header.duration.wrapper}>{duration}</div>
        </div>
        <div {...attrs.body.wrapper}>
          {leftSection}
          <div {...attrs.body.divider} />
          {rightSection}
        </div>
      </div>
    </ThreadDataSkeleton>
  );
};
